import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  React.useEffect(() => {
    document.title = "Pricing Plans - adiLessons";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Explore adiLessons pricing plans. Start a 14-day free trial on Starter, Growth, or School plans. Monthly and annual billing options available.');
  }, []);

  const handleBillingChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: 'monthly' | 'yearly' | null
  ) => {
    if (newPeriod !== null) {
      setBillingPeriod(newPeriod);
    }
  };

  const plans = [
    {
      name: 'Starter Plan',
      description: 'Ideal for independent approved instructors just starting out.',
      monthlyPrice: 10,
      yearlyPrice: 8.25, // £99 / year
      features: [
        'Calendar & Availability management',
        'Unlimited student bookings',
        'Two-way Google Calendar Sync',
        'Standard booking confirmations',
        'Student profile tracking',
      ],
      popular: false,
      ctaText: 'Start Free Trial',
    },
    {
      name: 'Growth Plan',
      description: 'Perfect for active ADIs wanting to manage diaries efficiently and reduce missed lessons.',
      monthlyPrice: 15,
      yearlyPrice: 12.42, // £149 / year
      features: [
        'Everything in Starter Plan',
        'Automated 24h WhatsApp Reminders',
        'DVSA 19-Topic Syllabus Progress Tracker',
        'Secure Student Payments (PayPal / Card)',
        'Lesson history & payment reporting',
        'Automated late cancellation alerts',
      ],
      popular: true,
      ctaText: 'Start Free Trial',
    },
    {
      name: 'School Plan',
      description: 'Built for multi-instructor driving schools and franchises.',
      monthlyPrice: 29,
      yearlyPrice: 24.08, // £289 / year
      features: [
        'Everything in Growth Plan',
        'Multi-instructor diaries & slots',
        'Personalized Subdomain (name.adilesson.co.uk)',
        'Custom themed website builders',
        '1-Click Lead-to-Pupil conversion tool',
        'Referral Campaign management systems',
      ],
      popular: false,
      ctaText: 'Contact Sales',
    }
  ];

  const faqs = [
    {
      question: 'Is there a free trial period?',
      answer: 'Yes! We offer a 14-day free trial on both the Starter and Growth plans. No credit card details are required to sign up and test the features.'
    },
    {
      question: 'How do automated WhatsApp reminders work?',
      answer: 'Once you link your phone number using the QR code in your dashboard, the system automatically dispatches booking confirmations and 24-hour reminders. Messages are sent at staggered, human-like intervals to ensure high deliverability and stay compliant with WhatsApp policy.'
    },
    {
      question: 'Can I cancel or change plans anytime?',
      answer: 'Absolutely. Subscriptions are billed on a rolling basis (monthly or yearly) and you can cancel, upgrade, or downgrade at any time directly from your billing portal.'
    },
    {
      question: 'Are online student payments secure?',
      answer: 'Yes. All payments processed through the app are processed via PCI-DSS compliant payment gateways (PayPal and Stripe). We do not store or process your students\' credit card information on our servers.'
    },
    {
      question: 'Do you charge transaction fees on student payments?',
      answer: 'adiLessons does not charge any additional transaction fees. Standard PayPal or Stripe merchant fees apply depending on your processing volume.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#2b3154',
          zIndex: 1100
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', height: 72 }}>
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2b3154', 
                  letterSpacing: '-0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif'
                }}
              >
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Header */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" color="primary" fontWeight="bold" sx={{ letterSpacing: '1.5px' }}>
            Pricing Plans
          </Typography>
          <Typography variant="h3" fontWeight="900" sx={{ mt: 1, mb: 2, color: '#0f162a', fontFamily: '"Outfit", sans-serif' }}>
            Simple, fair pricing for every business stage.
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4, fontWeight: 'normal' }}>
            Start managing your pupils and diaries on autopilot today. Cancel or switch plans at any time.
          </Typography>

          <ToggleButtonGroup
            value={billingPeriod}
            exclusive
            onChange={handleBillingChange}
            aria-label="billing period"
            sx={{
              bgcolor: 'white',
              p: 0.5,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '10px',
                px: 3,
                py: 1,
                fontWeight: 'bold',
                textTransform: 'none',
                '&.Mui-selected': {
                  bgcolor: '#1967D2',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1556b3',
                  }
                }
              }
            }}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">
              Yearly <span style={{ color: '#00FF84', marginLeft: '6px', fontWeight: '800' }}>-17%</span>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} alignItems="stretch" justifyContent="center">
          {plans.map((plan, index) => {
            const displayPrice = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
            const displayTotal = billingPeriod === 'monthly' ? `£${plan.monthlyPrice}/mo` : `£${Math.round(plan.yearlyPrice * 12)}/year`;

            return (
              <Grid size={{ xs: 12, md: 4 }} key={index} sx={{ display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    width: '100%',
                    p: 3,
                    borderRadius: 5,
                    border: plan.popular ? '2px solid #1967D2' : '1px solid #e2e8f0',
                    boxShadow: plan.popular ? '0 10px 30px rgba(25, 103, 210, 0.08)' : '0 4px 20px rgba(0, 0, 0, 0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    bgcolor: 'white',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  {plan.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 24,
                        bgcolor: '#1967D2',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        letterSpacing: '1px'
                      }}
                    >
                      MOST POPULAR
                    </Box>
                  )}

                  <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f162a', mb: 1 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 45, mb: 3 }}>
                      {plan.description}
                    </Typography>

                    <Box display="flex" alignItems="baseline" sx={{ mb: 3 }}>
                      <Typography variant="h3" fontWeight="800" sx={{ color: '#0f162a' }}>
                        £{displayPrice.toFixed(2)}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                        / instructor / mo
                      </Typography>
                    </Box>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 4, fontWeight: 'bold' }}>
                      Billed as {displayTotal}
                    </Typography>

                    <Divider sx={{ mb: 4 }} />

                    <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 4 }}>
                      {plan.features.map((feature, fIdx) => (
                        <Box display="flex" alignItems="flex-start" gap={1.5} key={fIdx}>
                          <CheckIcon sx={{ color: '#1967D2', fontSize: 20, mt: '2px' }} />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    onClick={() => navigate('/register')}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: '15px',
                      boxShadow: 'none',
                      bgcolor: plan.popular ? '#1967D2' : 'transparent',
                      color: plan.popular ? 'white' : '#1967D2',
                      borderColor: '#1967D2',
                      '&:hover': {
                        bgcolor: plan.popular ? '#1556b3' : 'rgba(25, 103, 210, 0.04)',
                        borderColor: '#1556b3',
                      }
                    }}
                  >
                    {plan.ctaText}
                  </Button>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* FAQs */}
        <Box sx={{ mt: 12, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h4" fontWeight="800" sx={{ textAlign: 'center', mb: 6, color: '#0f162a', fontFamily: '"Outfit", sans-serif' }}>
            Frequently Asked Questions
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                  bgcolor: 'white'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" />}
                  sx={{ py: 1, '& .MuiAccordionSummary-content': { my: 0 } }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#0f162a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pb: 3, px: 3, pt: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a', mt: 'auto' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved. adiLessons is operated under the brand name adiLessons. Contact: support@adilesson.co.uk.
        </Typography>
      </Box>
    </Box>
  );
};

export default PricingPage;
